import { useCallback, useEffect, useState } from 'react';
import type { ControlPosition } from '@vis.gl/react-google-maps';
import { MapControl, useMap } from '@vis.gl/react-google-maps';

import { hasFullscreenSupport } from '../../SwitchableMap.helpers';
import type { WkDocument, WkElement } from '../../SwitchableMap.types';

import MapExpanderControl from './MapExpanderControl';
import MapZoomControl from './MapZoomControl';

interface CustomMapControlsProps {
	expandPosition: ControlPosition;
	isInitiallyExpanded: boolean;
	isMapLoaded: boolean;
	setZoomLevel: React.Dispatch<React.SetStateAction<number>>;
	zoomLevel: number;
	zoomPosition: ControlPosition;
}

const FULLSCREEN_CHECK_DELAY = 100;

export const CustomMapControls: React.FC<CustomMapControlsProps> = ({
	expandPosition,
	isInitiallyExpanded,
	isMapLoaded,
	setZoomLevel,
	zoomLevel,
	zoomPosition,
}) => {
	const map = useMap();
	const [isExpanded, setIsExpanded] = useState(false);

	const handleZoom = (type: 'in' | 'out') => {
		const newZoom = type === 'in' ? zoomLevel + 1 : zoomLevel - 1;
		map?.setZoom(newZoom);
		setZoomLevel(newZoom);
	};

	const handleExpanding = useCallback(() => {
		const mapElement = map?.getDiv();
		if (!mapElement) return;

		const fullscreenElement = document.fullscreenElement || (document as WkDocument).webkitFullscreenElement;

		if (fullscreenElement) {
			void document?.exitFullscreen?.();
			if (!document?.exitFullscreen) (document as WkDocument)?.webkitExitFullscreen?.();
		} else {
			void mapElement?.requestFullscreen?.();
			if (!mapElement?.requestFullscreen) (mapElement as WkElement)?.webkitRequestFullscreen?.();
		}

		setTimeout(() => {
			setIsExpanded(Boolean(document.fullscreenElement || (document as WkDocument).webkitFullscreenElement));
		}, FULLSCREEN_CHECK_DELAY);
	}, [map]);

	useEffect(() => {
		if (isInitiallyExpanded && isMapLoaded) {
			handleExpanding();
		}
	}, [isInitiallyExpanded, isMapLoaded, handleExpanding]);

	if (!map) return null;

	return (
		<>
			{hasFullscreenSupport() && (
				<MapControl position={expandPosition}>
					<MapExpanderControl isExpanded={isExpanded} onClick={handleExpanding} />
				</MapControl>
			)}
			<MapControl position={zoomPosition}>
				<MapZoomControl onZoomIn={() => handleZoom('in')} onZoomOut={() => handleZoom('out')} zoomLevel={zoomLevel} />
			</MapControl>
		</>
	);
};
